// Shift and attendance DTOs (architecture sprint-13 §14.4, sprint-19 §8.3), shared with the client. No imports
// (shared-purity.test.ts), so the location and end-reason unions are repeated from shift-rules.ts.
// Sprint 19 removed the roster: no slot DTOs, and attendance is a plain list of shifts.
// Every instant is an ISO string (UTC); every LocalDate is 'YYYY-MM-DD'.

export type ShiftLocationValue = 'on_site' | 'remote';
export type ShiftEndReasonValue = 'ended' | 'auto_closed' | 'corrected';
export type WorkLocationValue = 'off' | 'optional' | 'required';

export interface ShiftProjectRef {
  id: string;
  name: string;
  color: string;
}

export interface ShiftDto {
  id: string;
  version: number;
  project: ShiftProjectRef;
  user: { id: string; name: string };
  startedAt: string;
  endedAt: string | null;
  /** Open shifts only: when the sweep will close it (sprint-19 §7). */
  autoCloseAt: string | null;
  location: ShiftLocationValue | null;
  locationSelfDeclared: boolean;
  endReason: ShiftEndReasonValue | null;
  createdVia: 'start' | 'correction';
  voided: boolean;
  /** A revision exists. */
  corrected: boolean;
}

export interface ShiftNoticeDto {
  shiftId: string;
  projectName: string;
  endedAt: string;
}

export interface StartOptionDto {
  project: ShiftProjectRef;
  workLocation: WorkLocationValue;
  /** The location of the caller's latest non-voided shift on this project, else null. */
  prefillLocation: ShiftLocationValue | null;
  /** Sprint 14: check-in alarms ring on this project's shifts (Start arms the audio first). */
  alarmsEnabled: boolean;
}

/** GET /api/shifts/mine */
export interface MyShiftsDto {
  shifts: ShiftDto[];
}

export interface AttendanceFlags {
  /** Auto-closed and no corrector has touched it since (shiftFlags). */
  autoClosed: boolean;
  corrected: boolean;
}

/** Sprint 14: OnShiftAlarmDto (repeated from alarm-rules.ts: src/shared modules have no dependencies). */
export interface AttendanceAlarmDto {
  lastConfirmedAt: string | null;
  nextMarkAt: string | null;
  appRunning: boolean;
  armed: boolean;
  device: 'laptop' | 'phone' | null;
  problems: Array<'missed' | 'app_not_running' | 'not_armed'>;
}

/** Sprint 14: CheckinSummaryDto (repeated from alarm-rules.ts). */
export interface AttendanceCheckinSummary {
  due: number;
  confirmed: number;
  late: number;
  missed: number;
  medianResponseSeconds: number | null;
}

export interface AttendanceRowDto {
  shift: ShiftDto;
  /** (endedAt ?? now) − startedAt, whole seconds, ≥ 0. */
  durationSeconds: number;
  flags: AttendanceFlags;
  /** Sprint 14: the shift's check-ins; null before 0007. */
  checkins: AttendanceCheckinSummary | null;
}

export interface AttendanceDto {
  range: { from: string; to: string };
  /** Sprint 14: `alarm` is null unless the project is alarm-active (and always before 0007). */
  onShiftNow: Array<{ shift: ShiftDto; elapsedSeconds: number; alarm: AttendanceAlarmDto | null }>;
  /** One per non-voided shift whose start is in the range (viewer's zone), by start, then name, then id. */
  rows: AttendanceRowDto[];
  /** Current active explicit members (any role) for "Add shift"; [] unless viewer.canCorrect. */
  members: Array<{ id: string; name: string }>;
  viewer: { canCorrect: boolean; canAddNotes: boolean };
}

export interface ShiftRevisionSnapshot {
  startedAt: string;
  endedAt: string | null;
  location: ShiftLocationValue | null;
  endReason: string | null;
}

export interface ShiftRevisionDto {
  id: string;
  action: 'added' | 'corrected' | 'voided';
  before: ShiftRevisionSnapshot | null;
  after: ShiftRevisionSnapshot & { voided?: true };
  reason: string;
  actor: { id: string; name: string };
  at: string;
}

export interface CorrectionWarnings {
  overlappingEntries: Array<{ date: string; startSecond: number; durationSeconds: number; projectName: string | null }>;
}

/** GET /api/projects items gain this (§5.3); null when shift mode is off. */
export interface ProjectShiftDto {
  enabled: boolean;
  workLocation: WorkLocationValue;
}

/** Note shift link (§13). */
export interface NoteShiftDto {
  id: string;
  startedAt: string;
  endedAt: string | null;
  voided: boolean;
}

/** The label of a shift entry in exports and on entry cards (§12). */
export const SHIFT_ENTRY_LABEL = 'Shift';
